<template>
  <div class="product-configurator" :key="productConfiguratorKey">
    <div
      v-for="option in configurableOptions"
      :key="option.uid"
      class="product-configurator__option"
    >
      <div class="product-configurator__option__name">
        <span>
          {{ translateAttributeCode(option.attribute_code) || option.label }}
        </span>
        <span
          v-if="
            (optionImage(option.values[0]) || hexCode(option.values[0])) &&
            productConfiguration.length > 0 &&
            selectedLabels[option.uid]
          "
        >
          {{ `- ${selectedLabels[option.uid]}` }}
        </span>
      </div>
      <div class="product-configurator__option__values">
        <div
          class="product-configurator__option__values__value"
          :class="{
            'product-configurator__option__values__value__with-image':
              optionImage(optionValue),
            'product-configurator__option__values__value__selected':
              flattenedProductConfiguration.includes(optionValue.uid),
          }"
          v-for="(optionValue, index) in option.values"
          :key="`product-configurator-value-${index}-${optionValue.uid}`"
          @click="
            $emit(
              'updateProductConfiguration',
              optionValue.label,
              optionValue.uid
            );
            selectedLabels[option.uid] = optionValue.label;
          "
        >
          <nuxt-img
            class="product-configurator__option__values__value__image"
            v-if="optionImage(optionValue)"
            :src="optionImage(optionValue)"
          />
          <div
            class="product-configurator__option__values__value__hex"
            v-else-if="hexCode(optionValue)"
            :style="{ backgroundColor: hexCode(optionValue) }"
          />
          <div
            class="product-configurator__option__values__value__plain"
            :class="
              flattenedProductConfiguration.includes(optionValue.uid) &&
              'product-configurator__option__values__value__plain__selected'
            "
            v-else
          >
            {{ optionValue.label }}
          </div>
        </div>
      </div>
    </div>
    <div class="product-configurator__additional-info">
      <div v-if="productDiameter">
        <span class="product-configurator__additional-info__label">
          {{ $t('Diameter') }}
        </span>
        <span>{{ productDiameter }}</span>
      </div>
      <div v-if="productHeight">
        <span class="product-configurator__additional-info__label">
          {{ $t('Height') }}
        </span>
        <span>{{ productHeight }}</span>
      </div>
      <div v-if="productWidth">
        <span class="product-configurator__additional-info__label">
          {{ $t('Width') }}
        </span>
        <span>{{ productWidth }}</span>
      </div>
      <div v-if="productDepth">
        <span class="product-configurator__additional-info__label">
          {{ $t('Depth') }}
        </span>
        <span>{{ productDepth }}</span>
      </div>
    </div>
    <template v-if="!product.status">
      <div class="product-configurator__disabled">
        {{ $t('Product no longer available') }}
      </div>
      <CustomCta
        theme="dark"
        class="product-configurator__cta"
        :is-full-width="true"
        @click="handleDisabledProductCtaClick"
      >
        <span>
          {{
            product.categories && product.categories.length > 0
              ? $t('Show me more products like this!')
              : $t('Go back')
          }}
        </span>
      </CustomCta>
    </template>
    <template v-else>
      <span class="product_availability" v-if="!isOnDemand && !isAvailable">{{
        $t('This product is not available')
      }}</span>
      <CustomCta
        theme="dark"
        class="product-configurator__cta"
        :is-full-width="true"
        :disabled="
          (!isOnDemand && (disableAddToCart || !isAvailable)) || addingToCart
        "
        @click="
          isOnDemand
            ? openModal('quote', { productName })
            : addItemToCart({ product, quantity: 1 })
        "
      >
        <span v-if="!addingToCart">
          {{ isOnDemand ? $t('Request a quote') : $t('Add to cart') }}
        </span>
        <LoadingDots v-else />
      </CustomCta>
      <CustomCta
        v-if="!isOnDemand && isAvailable"
        class="product-configurator__cta"
        :is-full-width="true"
        theme="dark"
        @click="openModal('pdp-contact-us', { productName })"
      >
        <span>
          {{ $t('Contact us') }}
        </span>
      </CustomCta>
      <CustomCta
        v-if="isCustomizable"
        theme="dark"
        class="product-configurator__cta"
        :is-full-width="true"
        @click="openModal('customization', { productName })"
      >
        <Ruler class="product-configurator__cta__ruler" />
        <span class="product-configurator__cta__customization">
          {{ $t('Request a customization') }}
        </span>
      </CustomCta>
      <EstimatedDelivery :product="product" />
    </template>
  </div>
</template>

<script>
import {
  defineComponent,
  computed,
  ref,
  onMounted,
  useRouter,
  useContext,
} from '@nuxtjs/composition-api';
import { productGetters } from '@gemini-vsf/composables';
import { useGtm, useMarkets } from '~/composables';
import { CustomCta } from '~/components/General';
import { flattenDeep } from 'lodash';
import EstimatedDelivery from './EstimatedDelivery.vue';
import {
  useCart,
  useUiState,
  useUiNotification,
  useAttributeCodeTranslation,
} from '~/composables';
import { useI18n } from '~/helpers/hooks/usei18n';
import { Ruler } from '~/components/General/Icons';
import LoadingDots from '~/components/General/LoadingDots.vue';

export default defineComponent({
  name: 'ProductConfigurator',
  components: {
    CustomCta,
    EstimatedDelivery,
    Ruler,
    LoadingDots,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
    productConfiguration: {
      type: Array,
      default: () => [],
    },
    defaultVariant: {
      type: Array,
      required: true,
    },
  },
  setup(props) {
    const {
      app: {
        $device: { isDesktop },
        localeRoute,
      },
    } = useContext();
    const router = useRouter();
    const { isFarEastMarket } = useMarkets();
    const { openModal, openSidebar } = useUiState();
    const { translateAttributeCode } = useAttributeCodeTranslation();
    const addingToCart = ref();
    const gtmIndex = ref(0);

    const desiredOrder = [
      'sdc_colore_vetro',
      'composizione_balls',
      'sdc_numero_luci',
      'paralume',
      'sdc_color_mount',
    ];

    const productConfiguratorKey = ref('');

    const configurableOptions = computed(() =>
      productGetters
        .getSortedConfigurableOptions(props.product)
        // sorts the configurable options based on the desiredOrder array
        .sort((a, b) => {
          const aIndex = desiredOrder.indexOf(a.attribute_code);
          const bIndex = desiredOrder.indexOf(b.attribute_code);
          return aIndex - bIndex;
        })
        .map((e) => ({
          ...e,
          values: e.values.sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { numeric: true })
          ),
        }))
        .filter(
          (configurableOption) =>
            !['sdc_size', 'sdc_lunghezza_catena'].includes(
              configurableOption.attribute_code
            )
        )
    );
    const productName = computed(
      () =>
        props.product.configurable_product_options_selection?.variant
          ?.merchant_sku || props.product.name
    );
    const { send: sendNotification } = useUiNotification();
    const trans = useI18n();
    const { addItem, cartError, cart } = useCart();
    const { actionCartGtmPush } = useGtm();

    const addItemToCart = async (productAndQuantity) => {
      addingToCart.value = true;
      await addItem(productAndQuantity);

      const { product } = productAndQuantity;
      if (cartError?.value?.addItem) {
        sendNotification({
          id: Symbol('cartError'),
          message: `${trans.t(cartError.value.addItem.message)}`,
          type: 'danger',
          icon: 'error',
          persist: false,
          title: 'Error',
        });
      } else {
        actionCartGtmPush(
          product,
          productAndQuantity.quantity,
          'add_to_cart',
          gtmIndex.value
        );
        gtmIndex.value++;
        isDesktop
          ? openSidebar('minicart')
          : sendNotification({
              id: Symbol(''),
              message: `${product.name} ${trans.t('added to cart')}`,
              type: 'success',
              icon: 'check',
              persist: false,
              title: 'Success',
            });
      }
      addingToCart.value = false;
    };

    const flattenedProductConfiguration = computed(() =>
      flattenDeep(props.productConfiguration)
    );

    const disableAddToCart = computed(
      () => !props.product?.configurable_product_options_selection?.variant
    );

    const cartItems = computed(() => cart?.value?.items);

    const isAvailable = computed(() => {
      const productQty =
        props.product?.configurable_product_options_selection.variant
          ?.only_x_left_in_stock || 0;
      const productQtyInCart =
        cartItems.value?.find((item) => {
          return (
            item?.product?.sku ===
            props.product?.configurable_product_options_selection?.variant?.sku
          );
        })?.quantity || 0;
      return productQty - productQtyInCart > 0;
    });

    const isOnDemand = computed(
      () =>
        props.product?.configurable_product_options_selection?.variant
          ?.sdc_ondemand || isFarEastMarket.value
    );
    const isCustomizable = computed(() => props.product?.sdc_custom_mirror);

    const selectedLabels = ref({});

    const optionImage = (optionValue) =>
      optionValue?.swatch_data[0]?.thumbnail?.url;

    const hexCode = (optionValue) => optionValue?.swatch_data[0]?.hex_code;

    const currentProductSelectedVariantData = computed(
      () => props.product?.configurable_product_options_selection?.variant
    );
    const productDiameter = computed(
      () =>
        currentProductSelectedVariantData.value?.sdc_diametro ||
        props.product?.sdc_diametro ||
        ''
    );
    const productHeight = computed(
      () =>
        currentProductSelectedVariantData.value?.sdc_altezza ||
        props.product?.sdc_altezza ||
        ''
    );
    const productWidth = computed(
      () =>
        currentProductSelectedVariantData.value?.sdc_larghezza ||
        props.product?.sdc_larghezza ||
        ''
    );
    const productDepth = computed(
      () =>
        currentProductSelectedVariantData.value?.sdc_profondita ||
        props.product?.sdc_profondita ||
        ''
    );

    onMounted(() => {
      props.defaultVariant.forEach((configurableOptionValue) => {
        const [attributeCode] = configurableOptionValue.split(':');
        const option = props.product.configurable_options.find(
          (o) => o.attribute_code === attributeCode
        );
        const optionValue = option.values.find(
          (v) => v.uid === configurableOptionValue
        ).label;
        selectedLabels.value[option.uid] = optionValue;
      });
      productConfiguratorKey.value = props.defaultVariant.uid;
    });

    const handleDisabledProductCtaClick = async () => {
      const urlKey = props?.product?.categories?.find(
        (c) => c.url_key
      )?.url_key;
      if (urlKey) {
        await router.push(localeRoute(`/${urlKey}`));
        return;
      }
      await router.push(localeRoute(`/`));
    };

    return {
      configurableOptions,
      optionImage,
      hexCode,
      addItem,
      openModal,
      addItemToCart,
      actionCartGtmPush,
      flattenedProductConfiguration,
      disableAddToCart,
      isCustomizable,
      selectedLabels,
      isOnDemand,
      productName,
      addingToCart,
      translateAttributeCode,
      productDiameter,
      productHeight,
      productWidth,
      productDepth,
      handleDisabledProductCtaClick,
      productConfiguratorKey,
      isAvailable,
    };
  },
});
</script>

<style lang="scss" scoped>
.product-configurator {
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  padding: 1.875rem 0 1 0;
  row-gap: 1.25rem;
  box-sizing: border-box;
  &__option {
    width: calc(100vw - 40px);
    &__name {
      @include paragraph-s;
      margin-bottom: 0.375rem;
    }
    &__values {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: flex-start;
      row-gap: 0.3125rem;
      &__value {
        width: calc((100% - 42px) / 6);
        font-size: 3.3vw;
        line-height: 17px;
        height: 2.25rem;
        display: flex;
        align-items: center;
        border: 1px solid var(--c-black);
        &:first-child,
        &:nth-child(7n) {
          border-top-left-radius: 2px;
          border-bottom-left-radius: 2px;
        }
        &:last-child,
        &:nth-child(6n) {
          border-top-right-radius: 2px;
          border-bottom-right-radius: 2px;
        }
        &__with-image {
          background-color: #ebebeb;
        }
        &__selected {
          border: 1px solid var(--c-black);
          border-radius: 2px 2px 2px 2px;
          &:has(> .product-configurator__option__values__value__plain) {
            background-color: var(--c-black);
          }
        }
        &__hex,
        &__plain,
        &__image {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        &__plain {
          display: block;
          overflow-wrap: break-word;
          height: auto;
          margin: 2px 3px;
          text-align: center;
          max-width: -webkit-fill-available;
          &__selected {
            background-color: var(--c-black);
            color: var(--c-white);
            border-width: 1px;
          }
        }
        &:hover {
          outline: 1px solid var(--c-black);
        }
      }
    }
  }
  &__additional-info {
    display: flex;
    gap: 1.3125rem;
    justify-content: flex-start;
    @include paragraph-s;
    &__label {
      color: var(--c-dark-gray);
    }
  }
  &__cta {
    &__ruler {
      transform: rotate(-45deg);
    }
    &__customization {
      padding-left: 0.625rem;
    }
    &:not(:hover) {
      svg.product-configurator__cta__ruler {
        rect,
        path {
          stroke: var(--c-white);
        }
      }
    }
  }
  &__disabled {
    text-transform: uppercase;
    @include label-s;
    width: fit-content;
    margin: 0 auto;
    color: #808080;
  }
}
@include from-desktop-min {
  .product-configurator {
    width: 100%;
    row-gap: 0.9375rem;
    &__option {
      width: 80%;
      &__values {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: flex-start;
        row-gap: 0.3125rem;
        width: 100%;
        position: relative;
        &__value {
          @include pointer;
          width: auto;
          height: 2rem;
          aspect-ratio: 2 / 1;
          font-size: 0.74vw;
          line-height: inherit;
          &:nth-child(7n) {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
          }
          &:nth-child(6n) {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
          }
          &:nth-child(9n) {
            border-top-left-radius: 2px;
            border-bottom-left-radius: 2px;
          }
          &:nth-child(8n) {
            border-top-right-radius: 2px;
            border-bottom-right-radius: 2px;
          }
        }
      }
    }
  }
}
</style>
